import React, { useRef } from "react";
import { useDrag, useDrop, XYCoord } from "react-dnd";
import { TableRow } from "@mui/material";

interface DraggableRowProps {
  id: number;
  index: number;
  moveRow?: (fromIndex: number, toIndex: number) => void;
  children: React.ReactNode;
  dropRow?: () => void;
}

interface DragItem {
  id: number;
  index: number;
}

const DraggableRow: React.FC<DraggableRowProps> = ({ id, index, moveRow, children, dropRow }) => {
  const ref = useRef<HTMLTableRowElement>(null);  // Ref ile satıra erişim sağlıyoruz

  const [, drop] = useDrop({
    accept: "row",
    hover: (item: DragItem, monitor) => {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      // Aynı satır üzerine bırakılmaya çalışılıyorsa işlem yapma
      if (dragIndex === hoverIndex) return;

      // Satırın alt ve üst kısmına göre sürükleme işlemi
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Mouse'un şu anki pozisyonunu alıyoruz
      const clientOffset = monitor.getClientOffset() as XYCoord;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Eğer ortadan daha üstteyse, sadece yukarı taşı
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      // Eğer ortadan daha alttaysa, sadece aşağı taşı
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      // Satırları taşır
      if (moveRow) {
        moveRow(dragIndex, hoverIndex);
        item.index = hoverIndex;  // Taşınan öğenin indexini günceller
      }
    },
    drop: () => {
      if (dropRow) dropRow();  // Taşınma işlemi tamamlandığında drop işlemi çağrılır
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: "row",
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));  

  return (
    <TableRow
      ref={ref}
      hover
      sx={{
        opacity: isDragging ? 0.4 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: 'background-color 0.15s ease',
        '&:last-child td': { borderBottom: 0 },
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.025)' },
      }}
    >
      {children}
    </TableRow>
  );
};

export default DraggableRow;
